import React, { useState } from "react";
import { Tabs, Tab, Box, MenuItem, Select, InputAdornment, SelectChangeEvent } from "@mui/material";
import { Link } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { CreateButton } from "../common/Button";

interface FilterState {
  dateRange: string;
  timeSlot: string;
  status: string;
  visitType: string;
  visitReason: string;
}

interface AppointmentLayoutProps {
  title?: string;
  showCreateButton?: boolean;
  createButtonText?: string;
  createButtonLink?: string;
  onCreateClick?: () => void;
  upcomingLabel?: string;
  pastLabel?: string;
  renderUpcomingContent: (filters: FilterState) => React.ReactNode;
  renderPastContent: (filters: FilterState) => React.ReactNode;
  showFilters?: boolean;
  customFilters?: React.ReactNode;
  containerStyle?: React.CSSProperties;
}

export const AppointmentLayout: React.FC<AppointmentLayoutProps> = ({
  title = "Appointments",
  showCreateButton = true,
  createButtonText = "+ Appointment",
  createButtonLink,
  onCreateClick,
  upcomingLabel = "Upcoming Appointments",
  pastLabel = "Past Appointments",
  renderUpcomingContent,
  renderPastContent,
  showFilters = true,
  customFilters,
  containerStyle = {},
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    dateRange: "",
    timeSlot: "",
    status: "",
    visitType: "",
    visitReason: "",
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleFilterChange = (field: keyof FilterState) => (
    event: SelectChangeEvent<string>
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCreateButtonClick = () => {
    if (onCreateClick) {
      onCreateClick();
    }
  };

  return (
    <div className="section" style={{ width: "100%", padding: "24px", ...containerStyle }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <h1
          style={{
            fontSize: "28px",
            fontWeight: 600,
            color: "#2D3748",
            margin: 0,
            fontFamily: "Quicksand",
          }}
        >
          {title}
        </h1>

        {showCreateButton && (
          <>
            {createButtonLink ? (
              <Link to={createButtonLink} style={{ textDecoration: "none" }}>
                <CreateButton handleCreateButtonClick={handleCreateButtonClick} createButtonText={createButtonText} />
              </Link>
            ) : (
              <CreateButton handleCreateButtonClick={handleCreateButtonClick} createButtonText={createButtonText} />
            )}
          </>
        )}
      </div>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "#E2E8F0", marginBottom: "24px" }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="appointment tabs"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#3B82F6",
              height: "3px",
            },
          }}
        >
          <Tab
            sx={{
              textTransform: "none",
              fontFamily: "Quicksand",
              fontWeight: 500,
              fontSize: "15px",
              color: "#A0AEC0",
              minWidth: "auto",
              paddingX: "20px",
              "&.Mui-selected": {
                color: "#2D3748",
                fontWeight: 600,
              },
            }}
            label={upcomingLabel}
          />
          <Tab
            sx={{
              textTransform: "none",
              fontFamily: "Quicksand",
              fontWeight: 500,
              fontSize: "15px",
              color: "#A0AEC0",
              minWidth: "auto",
              paddingX: "20px",
              "&.Mui-selected": {
                color: "#2D3748",
                fontWeight: 600,
              },
            }}
            label={pastLabel}
          />
        </Tabs>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            marginBottom: "24px",
            flexWrap: "wrap",
          }}
        >
          {customFilters || (
            <>
              {/* Date Range Filter */}
              <Select
                value={filters.dateRange}
                onChange={handleFilterChange("dateRange")}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ fontSize: 18, color: "#A0AEC0" }} />
                  </InputAdornment>
                }
                sx={{
                  minWidth: 180,
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  fontFamily: "Quicksand",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#CBD5E0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Select Dates
                </MenuItem>
                <MenuItem value="today" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Today
                </MenuItem>
                <MenuItem value="tomorrow" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Tomorrow
                </MenuItem>
                <MenuItem value="this-week" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  This Week
                </MenuItem>
                <MenuItem value="next-week" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Next Week
                </MenuItem>
              </Select>

              {/* Time Slot Filter */}
              <Select
                value={filters.timeSlot}
                onChange={handleFilterChange("timeSlot")}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ fontSize: 18, color: "#A0AEC0" }} />
                  </InputAdornment>
                }
                sx={{
                  minWidth: 160,
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  fontFamily: "Quicksand",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#CBD5E0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Select Time
                </MenuItem>
                <MenuItem value="morning" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Morning (6AM - 12PM)
                </MenuItem>
                <MenuItem value="afternoon" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Afternoon (12PM - 6PM)
                </MenuItem>
                <MenuItem value="evening" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Evening (6PM - 12AM)
                </MenuItem>
              </Select>

              {/* Status Filter */}
              <Select
                value={filters.status}
                onChange={handleFilterChange("status")}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke="#A0AEC0"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                  </InputAdornment>
                }
                sx={{
                  minWidth: 160,
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  fontFamily: "Quicksand",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#CBD5E0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Select Status
                </MenuItem>
                <MenuItem value="confirmed" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Confirmed
                </MenuItem>
                <MenuItem value="pending" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Pending
                </MenuItem>
                <MenuItem value="cancelled" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Cancelled
                </MenuItem>
              </Select>

              {/* Visit Type Filter */}
              <Select
                value={filters.visitType}
                onChange={handleFilterChange("visitType")}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ fontSize: 18, color: "#A0AEC0" }} />
                  </InputAdornment>
                }
                sx={{
                  minWidth: 160,
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  fontFamily: "Quicksand",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#CBD5E0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Visit Type
                </MenuItem>
                <MenuItem value="in-person" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  In Person Visit
                </MenuItem>
                <MenuItem value="virtual" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Virtual Visit
                </MenuItem>
              </Select>

              {/* Visit Reason Filter */}
              <Select
                value={filters.visitReason}
                onChange={handleFilterChange("visitReason")}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ fontSize: 18, color: "#A0AEC0" }} />
                  </InputAdornment>
                }
                sx={{
                  minWidth: 160,
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  fontFamily: "Quicksand",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#CBD5E0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Visit Reason
                </MenuItem>
                <MenuItem value="follow-up" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Follow Up
                </MenuItem>
                <MenuItem value="consultation" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Consultation
                </MenuItem>
                  <MenuItem value="new-user" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  New Issue
                </MenuItem>
                <MenuItem value="checkup" sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
                  Regular Checkup
                </MenuItem>
              </Select>
            </>
          )}
        </Box>
      )}

      {/* Tab Content */}
      <Box sx={{ backgroundColor: "#ffffff", borderRadius: "12px", padding: "16px" }}>
        {tabIndex === 0 && renderUpcomingContent(filters)}
        {tabIndex === 1 && renderPastContent(filters)}
      </Box>
    </div>
  );
};
